import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import { ClientSitePreview } from '../components/ClientSitePreview';
import { SeoHead } from '../components/SeoHead';
import { caseStudies } from '../data/caseStudies';

const detailLabels = [
  ['The ask', 'problem'],
  ['What Verno built', 'solution'],
  ['Delivered surface', 'result'],
] as const;

export function Demos() {
  const [featured, ...supporting] = caseStudies;

  return (
    <div className="portfolio-page">
      <SeoHead
        title="Selected Website Work | Verno Web"
        description="Inspect live website work from Verno Web, including Solariums Direct, Anderson Leadership Foundation, OrriaDesk, and PAX Learning Center."
        path="/demos"
      />

      <header className="portfolio-intro">
        <div className="portfolio-intro__forest" aria-hidden="true" />
        <div className="shoreline-wrap portfolio-intro__grid">
          <div className="portfolio-intro__copy">
            <h1>Real websites. Open them and judge the work.</h1>
            <p>Every project below is a published client site. Click through it, test the paths, and see the decisions in their real setting.</p>
            <Link className="shoreline-button shoreline-button--light" to="/free-demo">
              Request a free demo <ArrowRight aria-hidden="true" />
            </Link>
          </div>

          <a className="portfolio-intro__featured" href={featured.link} target="_blank" rel="noreferrer" aria-label={`Open the ${featured.title} website`}>
            <ClientSitePreview
              image={featured.screenshot}
              title={featured.title}
              url={featured.link}
              sizes="(min-width: 900px) 56vw, 100vw"
              loading="eager"
            />
            <span className="portfolio-intro__caption">
              <span><strong>{featured.title}</strong><small>{featured.type}</small></span>
              <ArrowUpRight aria-hidden="true" />
            </span>
          </a>
        </div>
      </header>

      <section className="portfolio-featured-details" aria-labelledby={`project-${featured.slug}`}>
        <div className="shoreline-wrap">
          <ProjectHeader study={featured} headingId={`project-${featured.slug}`} />
          <ProjectDetails study={featured} />
        </div>
      </section>

      <div className="portfolio-projects">
        {supporting.map((study, index) => (
          <article
            id={`work-${study.slug}`}
            key={study.slug}
            className={`portfolio-project ${index % 2 ? 'portfolio-project--light' : 'portfolio-project--dark'}`}
            aria-labelledby={`project-${study.slug}`}
          >
            <div className="shoreline-wrap">
              <ProjectHeader study={study} headingId={`project-${study.slug}`} />
              <a className="portfolio-project__preview" href={study.link} target="_blank" rel="noreferrer" aria-label={`Open the ${study.title} website`}>
                <ClientSitePreview
                  image={study.screenshot}
                  title={study.title}
                  url={study.link}
                  sizes="(min-width: 900px) 88rem, 100vw"
                />
              </a>
              <ProjectDetails study={study} />
            </div>
          </article>
        ))}
      </div>

      <section className="portfolio-close">
        <div className="shoreline-wrap">
          <h2>Your website can be the next one worth inspecting.</h2>
          <p>Start by mapping the offer, the buyer, and the lead path around it.</p>
          <Link className="shoreline-button" to="/free-demo">Request a free demo <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
}

function ProjectHeader({ study, headingId }: { study: (typeof caseStudies)[number]; headingId: string }) {
  return (
    <header className="portfolio-project__header">
      <div>
        <h2 id={headingId}>{study.title}</h2>
        <p>{study.type}</p>
      </div>
      <p>{study.tagline}</p>
      <a href={study.link} target="_blank" rel="noreferrer">Open the live website <ExternalLink aria-hidden="true" /></a>
    </header>
  );
}

function ProjectDetails({ study }: { study: (typeof caseStudies)[number] }) {
  return (
    <dl className="portfolio-project__details">
      {detailLabels.map(([label, key]) => (
        <div key={key}>
          <dt>{label}</dt>
          <dd>{study[key]}</dd>
        </div>
      ))}
    </dl>
  );
}
